import { ChangeEvent, useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import { useDispatch } from 'react-redux';

import {
  BeneficiaryDrawerAccordionDetailsStyled,
  BeneficiaryDrawerAccordionStyled,
  BeneficiarySelectCardStyled,
} from './BeneficiarySelectCardStyled';
import { BeneficiarySelectedOptionsDto } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery/types';
import { PsButtonTuner, PsCheckboxTuner, PsNumericTuner } from 'stencilSetup';
import { replaceFirstSpaceWithDash } from 'enrollment/features/Benefits/BenefitsPreview/GroupedBenefits/utils';
import { MetadataDto } from 'theSource/types/dtos';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { FormElement } from 'enrollment/components/Shared/FormElement';
import { getInitialValuesFromBeneficiary, mapBeneficiaryModel } from 'theSource/utils';
import { addNewBeneficiary } from '../../services/slice';
import { generateRandomSixDigitNumber } from 'enrollment/utils/common/Utils';
import './BeneficiarySelectCard.css';





interface BeneficiarySelectCardProps {
  orgBenefitId: number;
  beneficiary: BeneficiarySelectedOptionsDto;
  isPrimary: boolean;
  checkboxStates: Record<string, boolean>;
  setCheckboxStates: (e: any) => void;
  hideCheckBox?: boolean;
  inputAllocationField: { [key: string]: string };
  setInputAllocationField: (e: any) => void;
  addToAllBenefits: Record<string, boolean>;
  setAddToAllBenefits: (e: any) => void;
  handleAddToAllBenefits?: (e: any) => void;
  coverageLineId?: number;
  metadata: MetadataDto;
}

export function BeneficiarySelectCard({
  orgBenefitId,
  beneficiary,
  isPrimary,
  checkboxStates,
  setCheckboxStates,
  hideCheckBox = false,
  inputAllocationField,
  setInputAllocationField,
  addToAllBenefits,
  setAddToAllBenefits,
  coverageLineId,
  handleAddToAllBenefits,
  metadata
}: BeneficiarySelectCardProps) {
  const { name, relationship } = beneficiary;
  const firstFocusableRef = useRef<HTMLInputElement | null>(null);
  const dispatch = useDispatch();

  const formattedName = replaceFirstSpaceWithDash(name);

  const checkboxId = `checkbox-${name.replace(/\s+/g, '')}-${relationship}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`;
  const inputAllocationId = `allocation_${formattedName}_${relationship}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`;
  const addToAllBenefitsId = `add-to-all-benefit_${formattedName}_${relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`;

  const [openEditMissingDrawer, setOpenEditMissingDrawer] = useState(false);

  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');


  const categories = useMemo(() => mapBeneficiaryModel(metadata), [metadata]);


  const initialValues = useMemo(
    () => getInitialValuesFromBeneficiary(categories, beneficiary),
    [categories, beneficiary]
  );

  const [updatedValues, setUpdatedValues] = useState(initialValues);
  const updatedValuesRef = useRef(updatedValues);
  const [saveDisabled, setSaveDisabled] = useState(true);

  useEffect(() => {
    updatedValuesRef.current = updatedValues;
  }, [updatedValues]);

  useEffect(() => {
    if (openEditMissingDrawer) {
      setUpdatedValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditMissingDrawer, initialValues]);

  useEffect(() => {
    if (!openEditMissingDrawer) { return; }

    // Find all required fields (except allocation and primary_beneficiary)
    const requiredFields = categories
      .flatMap((category: any) => category.fields)
      .filter(
        (field: any) =>
          field.required
      );


    // Check if any required field is missing
    const anyMissing = requiredFields.some(
      (field: any) =>
        updatedValues[field.fieldName] === undefined ||
        updatedValues[field.fieldName] === null ||
        updatedValues[field.fieldName] === ''
    );

    setSaveDisabled(anyMissing);
  }, [updatedValues, openEditMissingDrawer, categories]);


  const metadataArray = Object.entries(metadata || {}).map(
    ([fieldName, meta]) => ({
      fieldName: fieldName as keyof BeneficiarySelectedOptionsDto,
      ...meta,
    })
  );


  const missingRequiredFields = metadataArray
    .filter((field) => field.required
      && field.fieldName !== 'allocation')
    .filter((field) => {
      const value = updatedValues[field.fieldName as keyof typeof beneficiary];
      return value === undefined || value === null || value === '';
    })
    .map((field) => field.caption as string);

  const handleSubmitEditDrawer = () => {
    const values = updatedValuesRef.current;

    // Normalize values: replace empty strings/nulls with null, convert to string
    const valuesWithEmptyStringsReplaced = Object.entries(values).reduce(
      (newValues, [key, value]) => {
        newValues[key] =
          value === '' || value === null ? null : value!.toString();
        return newValues;
      },
      {} as { [key: string]: string | null }
    );

    // Build the updated beneficiary object
    const beneficiaryAdded = {
      ...beneficiary,
      ...valuesWithEmptyStringsReplaced,
      primary_beneficiary: isPrimary,
      allocation: valuesWithEmptyStringsReplaced.allocation,
      uniq_id: generateRandomSixDigitNumber(),
      coverage_line_id: coverageLineId,
    };
    const drawer = document.getElementById(`edit-missing-fields-drawer-${checkboxId}`);
    let addToAllBenefitsCheckbox: HTMLInputElement | null = null;
    if (drawer) {
      addToAllBenefitsCheckbox = drawer.querySelector(
        `#add-to-all-benefit-drawer_${checkboxId}`
      ) as HTMLInputElement | null;
    }

    if (addToAllBenefitsCheckbox?.checked) {
      handleAddToAllBenefits?.(beneficiaryAdded);
    } else {
      dispatch(addNewBeneficiary({ orgBenefitId, beneficiaryAdded }));
    }

    // Close the drawer
    setOpenEditMissingDrawer(false);
    setCheckboxStates((prev: any) => ({
      ...prev,
      [checkboxId]: false,
    }));
  };

  const handleAccordionCheckbox = (e: any) => {
    if (e?.target?.tagName === 'INPUT') {
      const isChecked = e?.target?.checked;

      setCheckboxStates((prevState: any) => ({
        ...prevState,
        [e?.target?.id]: isChecked,
      }));

      if (
        isChecked ||
        inputAllocationField[inputAllocationId] !== '' ||
        !addToAllBenefits[addToAllBenefitsId]
      ) {
        setInputAllocationField((prevInputAllocationField: any) => ({
          ...prevInputAllocationField,
          [inputAllocationId]: '',
        }));
        setAddToAllBenefits((prevState: any) => ({
          ...prevState,
          [addToAllBenefitsId]: false,
        }));
      }
    }
  };

  const handleFieldValidityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUpdatedValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleInputField = (e: ChangeEvent<HTMLInputElement>) => {
    setInputAllocationField((prevInputAllocationField: any) => ({
      ...prevInputAllocationField,
      [e?.target?.id]: e?.target?.value,
    }));
  };

  const handleAccordionAddToAllBenefits = (e: any) => {
    if (e?.target?.tagName === 'INPUT') {
      setAddToAllBenefits((prevState: any) => ({
        ...prevState,
        [e?.target?.id]: e?.target?.checked,
      }));
    }
  };

  const isAccordionOpen = checkboxStates[checkboxId];

  useEffect(() => {
    if (isAccordionOpen && firstFocusableRef.current) {
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 0);
    }
  }, [isAccordionOpen]);

  const initiallyMissingFields = useMemo(() => {
    return categories
      .flatMap((category: any) => category.fields)
      .filter((field: any) =>
        initialValues[field.fieldName] === undefined ||
        initialValues[field.fieldName] === null ||
        initialValues[field.fieldName] === ''
      )
      .map((field: any) => field.fieldName);
  }, [categories, initialValues]);

  return (
    <>
      <OrbitDrawer
        drawerId={`edit-missing-fields-drawer-${checkboxId}`}
        heading='Edit Missing Fields'
        isOpen={openEditMissingDrawer}
        footer={true}
        saveButton={t('common:save')}
        handleSubmit={handleSubmitEditDrawer}
        saveButtonDisabled={saveDisabled}
        handleClose={() => setOpenEditMissingDrawer(false)}
      >
        <Box
          component="form"
          id={`edit-missing-fields-form-${checkboxId}`}
        >
          <p>Please provide the missing required information for this beneficiary to continue.</p>
          <div>
            {categories.map((category: any, index: number) => (
              <div key={index}>
                {category.fields.map((field: any) => {
                  // Only allow editing missing fields, disable filled ones
                  const isInitiallyMissingFields = initiallyMissingFields.includes(field.fieldName);
                  if (field.fieldName === 'allocation') {
                    return (
                      <FormElement
                        key={field.fieldName}
                        inputId={`${field.fieldName}-${checkboxId}`}
                        formField={field.dataType}
                        label={field.label}
                        name={field.fieldName}
                        value={updatedValues[field.fieldName]}
                        required={field.required ? true : false}
                        disabled={!isInitiallyMissingFields}
                        onInput={isInitiallyMissingFields ? handleFieldValidityChange : undefined}
                        validationText="Must be a number between 0 and 100 and can have up to 2 decimal places"
                        icon="percent"
                      />
                    );
                  } else if (field.fieldName === 'primary_beneficiary') {
                    return (
                      <ps-checkbox
                        data-testid={field.fieldName}
                        input-id={`${field.fieldName}-${checkboxId}`}
                        label={field.label}
                        name={field.fieldName}
                        checked={isPrimary ? true : null}
                        natural
                        extra-class="d-none"
                        key={field.fieldName}
                      ></ps-checkbox>
                    );
                  } else {
                    return (
                      <FormElement
                        key={field.fieldName}
                        inputId={`${field.fieldName}-${checkboxId}`}
                        formField="text"
                        label={field.label}
                        name={field.fieldName}
                        value={updatedValues[field.fieldName]}
                        required={field.required ? true : false}
                        disabled={!isInitiallyMissingFields}
                        onInput={isInitiallyMissingFields ? handleFieldValidityChange : undefined}
                      />
                    );
                  }
                })}

                <div className="add-to-all-benefits-row">
                  <span className="add-to-all-benefits-label">
                    {t('addToAllBenefits')}
                  </span>
                  <PsCheckboxTuner
                    data-testid="add-to-all-benefit"
                    inputId={`add-to-all-benefit-drawer_${checkboxId}`}
                    name={`add-to-all-benefit_${name.replace(' ', '-')}_${relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`}
                    label={t('addToAllBenefits')}
                    hideLabel
                    key={`add-to-all-benefit_${name.replace(' ', '-')}_${relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`}
                    className="mobile-drawer-checkbox"
                    extraClass="mt-n2 mb-2"
                  />
                </div>
                
              </div>
            ))}
          </div>
        </Box>
      </OrbitDrawer>
      <BeneficiaryDrawerAccordionStyled
        sx={{
          border: checkboxStates[checkboxId]
            ? '1px solid #5C70A1'
            : '1px solid #E6E6E6',
        }}
        id={`accordion-${name}-${relationship}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
      >
        <div className="py-2_5 px-3">
          <BeneficiarySelectCardStyled>
            <div>
              <p className="semiBold mb-0">{name}</p>
              <p className="body2 mb-0">{relationship}</p>
            </div>
            <div>
              <PsCheckboxTuner
                data-testid="select-dependent-checkbox-card"
                inputId={checkboxId}
                name={name}
                label={name}
                onClick={handleAccordionCheckbox}
                value={checkboxStates[checkboxId]}
                checked={checkboxStates[checkboxId]}
                hideLabel
                key={checkboxId}
              />
            </div>
          </BeneficiarySelectCardStyled>

        </div>
        {checkboxStates[checkboxId] && (
          <BeneficiaryDrawerAccordionDetailsStyled>


            {/* <div className="my-auto">
              <p className="body2 text-nowrap mr-4 mb-0">
                {t('definedAllocationMobileViewText')}
              </p>
            </div> */}
            {missingRequiredFields.length > 0 && (
              <>
                <div style={{ color: '#e30d0d', margin: 8, fontSize: '1.2rem' }}>
                  Beneficiary is missing required fields.
                </div>
                <PsButtonTuner
                  buttonId={`edit-missing-fields-btn-${checkboxId}`}
                  label='Edit Missing Fields'
                  level="secondary"
                  iconRight="caret-right"
                  onClick={() => {
                    setOpenEditMissingDrawer(true);
                  }}
                  style={{ marginTop: 8 }}
                />
              </>
            )}
            {missingRequiredFields.length === 0 && (
              <>
                {!hideCheckBox && (
                  <div className="add-to-all-benefits-row">
                    <span className="add-to-all-benefits-label">
                      {t('addToAllBenefits')}
                    </span>
                    <PsCheckboxTuner
                      data-testid="add-to-all-benefit"
                      inputId={addToAllBenefitsId}
                      name={`add-to-all-benefit_${name.replace(' ', '-')}_${relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`}
                      label={t('addToAllBenefits')}
                      hideLabel
                      onClick={handleAccordionAddToAllBenefits}
                      onPsChange={handleAccordionAddToAllBenefits}
                      value={addToAllBenefits[addToAllBenefitsId]}
                      checked={addToAllBenefits[addToAllBenefitsId]}
                      key={`add-to-all-benefit_${name.replace(' ', '-')}_${relationship}-${orgBenefitId}${coverageLineId ? '-' + coverageLineId : ''}`}
                      className="mobile-drawer-checkbox"
                      extraClass="mt-n2 mb-2"
                    />
                  </div>
                )}
                <PsNumericTuner
                  inputId={`allocation_${name.replace(' ', '-')}_${relationship}-${orgBenefitId}-${isPrimary}${coverageLineId ? '-' + coverageLineId : ''}`}
                  name={t('allocation')}
                  label={t('definedAllocationMobileViewText')}
                  value={inputAllocationField[inputAllocationId]}
                  inlineLabel
                  inlineSmall
                  validationText="must be a number must be between 0 and 100 and can have up to 2 decimal places."
                  onBlur={(e) => handleInputField(e as any)}
                  className='existing-beneficiary-label-text-allocation'
                // onMouseOut={(e: any) => e.target.blur()}
                />
              </>
            )}
          </BeneficiaryDrawerAccordionDetailsStyled>
        )}
      </BeneficiaryDrawerAccordionStyled>
    </>
  );
}
